/**
 * Role 15 — Analysis of Profit or Loss by Nature — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role15-320100 (Analysis of Profit or Loss by Nature) totals.
 *
 * 15 calculated rows (revenue, other income, employee benefits, expense aggregations,
 * finance income). Each is a sum over a fixed list of line items per suffix.
 *
 * Note: a few aggregations (`ifrs-full:OtherIncome`, `ifrs-full:OtherExpenseByNature`)
 * include other CALCULATED fields in their addend lists (e.g. OtherIncome includes
 * GainsOnForeignExchange which is itself computed earlier). Order matters — the
 * component's sequence is preserved so each calculated value is available in
 * `calculatedTotals` *before* it is read by a later aggregation.
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes (e.g. 'GroupCurrent', 'Current')
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_320100(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Read either a freshly calculated total OR a raw form value.
  // The component reads back its own intermediate totals via the form
  // (`getFieldValue` → `analysisForm.get(qname + suffix).value`). To preserve
  // that semantic without a real form, the service prefers the just-computed
  // calculatedTotals entry, falling back to formData.
  const num = (key: string): number => {
    const fromCalc = calculatedTotals[key];
    if (fromCalc !== undefined) {
      return typeof fromCalc === 'number'
        ? fromCalc
        : parseFloat(String(fromCalc)) || 0;
    }
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sum = (qnames: ReadonlyArray<string>, suffix: string): number =>
    qnames.reduce((acc, qname) => acc + num(qname + suffix), 0);

  for (const suffix of suffixes) {
    // REVENUE SECTION

    // Total revenue from sale of goods
    calculatedTotals[`ifrs-full:RevenueFromSaleOfGoods${suffix}`] = sum(
      [
        'ifrs-full:RevenueFromSaleOfTelecommunicationEquipment',
        'ssmt-mfrs:RevenueFromSaleOfProperty',
        'ssmt-mfrs:RevenueFromSaleOfConstructionContracts',
        'ssmt-mfrs:RevenueFromSaleOfCleanWaterTreatmentAndDisposalOfWasteWater',
        'ifrs-full:RevenueFromSaleOfFoodAndBeverage',
        'ifrs-full:RevenueFromSaleOfAgriculturalProduce',
        'ifrs-full:RevenueFromSaleOfOilAndGasProducts',
        'ssmt-mfrs:RevenueFromSaleOfOtherGoods',
      ],
      suffix,
    );

    // Total revenue from rendering of services
    calculatedTotals[`ifrs-full:RevenueFromRenderingOfServices${suffix}`] =
      sum(
        [
          'ssmt-mfrs:RevenueFromEntertainmentOperations',
          'ifrs-full:RevenueFromRenderingOfTelecommunicationServices',
          'ifrs-full:RevenueFromRenderingOfTransportServices',
          'ifrs-full:RevenueFromRenderingOfInformationTechnologyServices',
          'ssmt-mfrs:RevenueFromEducationalServices',
          'ssmt-mfrs:RevenueFromHealthcareServices',
          'ifrs-full:RevenueFromRenderingOfCargoAndMailTransportServices',
          'ssmt-mfrs:RevenueFromRenderingOfOtherServices',
        ],
        suffix,
      );

    // Total interest income
    calculatedTotals[`ifrs-full:RevenueFromInterest${suffix}`] = sum(
      [
        'ifrs-full:InterestIncomeOnLoansAndReceivables',
        'ifrs-full:InterestIncomeOnOtherFinancialAssets',
      ],
      suffix,
    );

    // Total other fee and commission income
    calculatedTotals[`ifrs-full:FeeAndCommissionIncome${suffix}`] = sum(
      [
        'ifrs-full:BrokerageFeeIncome',
        'ifrs-full:PortfolioAndOtherManagementFeeIncome',
        'ifrs-full:OtherFeeAndCommissionIncome',
      ],
      suffix,
    );

    // TOTAL REVENUE
    calculatedTotals[`ifrs-full:Revenue${suffix}`] = sum(
      [
        'ifrs-full:RevenueFromSaleOfGoods',
        'ifrs-full:RevenueFromRenderingOfServices',
        'ifrs-full:RevenueFromInterest',
        'ifrs-full:FeeAndCommissionIncome',
        'ifrs-full:RevenueFromDividends',
        'ifrs-full:RentalIncome',
        'ifrs-full:RevenueFromRoyalties',
        'ifrs-full:OtherRevenue',
      ],
      suffix,
    );

    // OTHER INCOME SECTION

    // Total foreign exchange gain
    calculatedTotals[`ssmt-mfrs:GainsOnForeignExchange${suffix}`] = sum(
      [
        'ssmt-mfrs:RealisedGainOnForeignExchange',
        'ssmt-mfrs:UnrealisedGainOnForeignExchange',
      ],
      suffix,
    );

    // Total gain on disposal of subsidiaries, associates and JVs
    calculatedTotals[
      `ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates${suffix}`
    ] = sum(
      [
        'ssmt-mfrs:GainsFromDisposalOfSubsidiaries',
        'ssmt-mfrs:GainsFromDisposalOfAssociates',
        'ssmt-mfrs:GainsFromDisposalOfJointVentures',
      ],
      suffix,
    );

    // Total reversal of impairment loss
    calculatedTotals[
      `ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss${suffix}`
    ] = sum(
      [
        'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossLoansAndAdvances',
        'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossInvestmentProperty',
        'ssmt-mfrs:ReversalOfImpairmentLossInLandHeldForDevelopment',
        'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
        'ssmt-mfrs:ReversalOfImpairmentLossForOtherAssets',
      ],
      suffix,
    );

    // TOTAL OTHER INCOME
    calculatedTotals[`ifrs-full:OtherIncome${suffix}`] = sum(
      [
        'ssmt-mfrs:AmortisationOfDeferredIncome',
        'ssmt-mfrs:BadDebtsRecovered',
        'ssmt-mfrs:OtherIncomeDividend',
        'ssmt-mfrs:OtherIncomeRoyaltyAndFranchise',
        'ssmt-mfrs:WriteBackOfFuelCost',
        'ifrs-full:IncomeFromReimbursementsUnderInsurancePolicies',
        'ssmt-mfrs:GrantsOrIncentivesReceivedByMalaysianGovernmentOrAgencies',
        'ssmt-mfrs:GrantsOrIncentivesReceivedByForeignGovernmentOrAgencies',
        'ssmt-mfrs:ContributionsReceivedByLocalContributor',
        'ssmt-mfrs:ContributionsReceivedByForeignContributor',
        'ssmt-mfrs:ContributionsReceivedByUnknownContributor',
        'ssmt-mfrs:GainsOnForeignExchange',
        'ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
        'ssmt-mfrs:GainsOnDisposalOfOtherInvestments',
        'ifrs-full:GainsOnDisposalsOfPropertyPlantAndEquipment',
        'ifrs-full:GainsOnDisposalsOfNoncurrentAssets',
        'ssmt-mfrs:OtherGainsRecognisedInProfitOrLossFairValueMeasurementAssets',
        'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
        'ifrs-full:ReversalOfInventoryWritedown',
        'ssmt-mfrs:OtherInterestIncome',
        'ssmt-mfrs:IncomeFromManagementFees',
        'ssmt-mfrs:NetDepositsRecognised',
        'ifrs-full:GainsOnChangeInFairValueOfDerivatives',
        'ssmt-mfrs:FairValueGainOnRecycleOfForexReserveUponDisposalOfSubsdiaries',
        'ssmt-mfrs:OtherFeesAndCommissionIncome',
        'ssmt-mfrs:OtherRentalIncome',
        'ssmt-mfrs:OtherRentalIncomeOnLandAndBuildings',
        'ifrs-full:MiscellaneousOtherOperatingIncome',
      ],
      suffix,
    );

    // EMPLOYEE BENEFITS EXPENSE
    calculatedTotals[`ssmt-mfrs:EmployeeBenefitsExpenseByNature${suffix}`] =
      sum(
        [
          'ifrs-full:WagesAndSalaries',
          'ssmt-mfrs:BonusExpenses',
          'ifrs-full:ExpenseFromSharebasedPaymentTransactionsWithEmployees',
          'ssmt-mfrs:EmployeeShareBasedPayment',
          'ifrs-full:SocialSecurityContributions',
          'ifrs-full:PostemploymentBenefitExpenseDefinedBenefitPlans',
          'ifrs-full:PostemploymentBenefitExpenseDefinedContributionPlans',
          'ifrs-full:OtherLongtermBenefits',
          'ifrs-full:OtherShorttermEmployeeBenefits',
          'ifrs-full:OtherEmployeeExpense',
        ],
        suffix,
      );

    // OTHER EXPENSES SECTION

    // Total auditor's remuneration
    calculatedTotals[`ifrs-full:AuditorsRemuneration${suffix}`] = sum(
      [
        'ifrs-full:AuditorsRemunerationForAuditServices',
        'ifrs-full:AuditorsRemunerationForOtherServices',
      ],
      suffix,
    );

    // Total loss on disposal of subsidiaries, associates and JVs
    calculatedTotals[
      `ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures${suffix}`
    ] = sum(
      [
        'ssmt-mfrs:LossOnDisposalOfSubsidiaries',
        'ssmt-mfrs:LossOnDisposalOfAssociates',
        'ssmt-mfrs:LossOnDisposalOfJointVentures',
      ],
      suffix,
    );

    // Total director's remuneration
    calculatedTotals[`ssmt-mfrs:DirectorsRemuneration${suffix}`] = sum(
      [
        'ssmt-mfrs:WagesAndSalariesToDirectors',
        'ssmt-mfrs:BonusToDirectors',
        'ssmt-mfrs:BenefitsInKindExpenses',
        'ssmt-mfrs:DirectorsFees',
        'ssmt-mfrs:PerformanceIncentives',
        'ssmt-mfrs:RetirementBenefitsDefinedBenefitPlans',
        'ssmt-mfrs:RetirementBenefitsDefinedContributionPlans',
        'ifrs-full:KeyManagementPersonnelCompensationSharebasedPayment',
        'ssmt-mfrs:OtherDirectorsRemuneration',
      ],
      suffix,
    );

    // TOTAL OTHER EXPENSES
    calculatedTotals[`ifrs-full:OtherExpenseByNature${suffix}`] = sum(
      [
        'ifrs-full:AuditorsRemuneration',
        'ifrs-full:AmortisationExpense',
        'ifrs-full:DepreciationPropertyPlantAndEquipment',
        'ssmt-mfrs:NaturalDisasterRelatedExpenses',
        'ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
        'ifrs-full:LossesOnDisposalsOfInvestments',
        'ifrs-full:LossesOnDisposalsOfPropertyPlantAndEquipment',
        'ifrs-full:LossesOnDisposalsOfNoncurrentAssets',
        'ssmt-mfrs:OtherLossesRecognisedInProfitOrLossFairValueMeasurementAssets',
        'ifrs-full:RentalExpense',
        'ssmt-mfrs:ResearchAndDevelopmentExpensesNature',
        'ifrs-full:RoyaltyExpense',
        'ssmt-mfrs:DirectorsRemuneration',
        'ssmt-mfrs:OtherMiscellaneousExpenses',
      ],
      suffix,
    );

    // FINANCE INCOME
    calculatedTotals[`ifrs-full:FinanceIncome${suffix}`] = sum(
      [
        'ssmt-mfrs:InterestIncomeDueFromRelatedParties',
        'ifrs-full:OtherFinanceIncome',
      ],
      suffix,
    );
  }

  return calculatedTotals;
}
